import { useEffect, useState } from 'react';

import { Box, HStack, Stack, Text } from '@chakra-ui/react';

import { Button } from '@m/components/ui/button';
import { ListRow, SectionHeader } from '@m/components/ui/mobile-rows';
import { Radio, RadioGroup } from '@m/components/ui/radio';
import { DirectionsCar } from '@m/icons';
import { Vehicle } from '@m/types/api/Vehicle';
import { heapTrackProperties } from '@m/utils/heap';

import { PayHeader } from '../PayHeader/PayHeader';

interface MemberConversionFlowProps {
  onContinue: (linkVehicle: boolean) => void;
  userId?: number;
  vehicle: Vehicle;
  visitId?: number;
}

export function MemberConversionFlow({
  vehicle,
  onContinue,
  visitId,
  userId,
}: MemberConversionFlowProps) {
  const [selectedValue, setSelectedValue] = useState<string>('');

  useEffect(() => {
    heapTrackProperties('text-to-pay/member-conversion:viewed', {
      'timestamp(utc)': new Date().toUTCString(),
      timestamp: Date.now(),
      transactionId: visitId,
      userId,
    });
  }, [visitId, userId]);

  const handleSubmit = () => {
    const linkVehicle = selectedValue === 'yes';

    heapTrackProperties('text-to-pay/member-conversion:submitted', {
      'timestamp(utc)': new Date().toUTCString(),
      timestamp: Date.now(),
      transactionId: visitId,
      userId,
      linkVehicle,
      selection: selectedValue,
    });

    if (selectedValue === 'yes') {
      onContinue(true);
    } else if (selectedValue === 'no') {
      onContinue(false);
    }
  };

  return (
    <Stack gap={0} width="100%">
      <PayHeader />

      <Box px={4}>
        <SectionHeader title="Use this vehicle for future visits?">
          Select 'Yes' to save vehicle to your account - no more stopping at the gate any time you
          park with Metropolis.
        </SectionHeader>
      </Box>

      <Box px={4}>
        <ListRow title="License plate" icon={<DirectionsCar />}>
          {vehicle.licensePlate.text}
        </ListRow>
      </Box>

      <Box px={4} py={4}>
        <RadioGroup value={selectedValue} onValueChange={(e) => setSelectedValue(e.value || '')}>
          <Stack gap={2}>
            <Box
              borderWidth={selectedValue === 'yes' ? '2px' : '1px'}
              borderColor="stroke.border.default"
              borderRadius="sm"
              p={4}
              cursor="pointer"
              onClick={() => setSelectedValue('yes')}
              boxSizing="border-box"
            >
              <HStack gap={4} alignItems="center">
                <Radio value="yes" />
                <Stack gap={1} flex={1}>
                  <Text variant="MainBody" fontWeight="600" color="text.primary">
                    Yes
                  </Text>
                  <Text variant="SmallBody" color="text.secondary">
                    I use this vehicle regularly.
                  </Text>
                </Stack>
              </HStack>
            </Box>

            <Box
              borderWidth={selectedValue === 'no' ? '2px' : '1px'}
              borderColor="stroke.border.default"
              borderRadius="sm"
              p={4}
              cursor="pointer"
              onClick={() => setSelectedValue('no')}
              boxSizing="border-box"
            >
              <HStack gap={4} alignItems="center">
                <Radio value="no" />
                <Stack gap={1} flex={1}>
                  <Text variant="MainBody" fontWeight="600" color="text.primary">
                    No
                  </Text>
                  <Text variant="SmallBody" color="text.secondary">
                    This is a rental or temporary vehicle.
                  </Text>
                </Stack>
              </HStack>
            </Box>
          </Stack>
        </RadioGroup>
      </Box>

      <Box px={4} pb={4} position="absolute" width="100%" bottom="0px">
        <Button
          variant="primary"
          size="2xl"
          width="100%"
          disabled={!selectedValue}
          onClick={handleSubmit}
        >
          Continue
        </Button>
      </Box>
    </Stack>
  );
}
